import {
  AllergyChipInput,
  parseAllergiesJson,
  serializeAllergiesJson,
} from '@/components/AllergyChipInput';
import {
  MedicationEditor,
  parseMedicationsJson,
  serializeMedicationsJson,
} from '@/components/MedicationEditor';
import type { Medication } from '@/components/MedicationEditor';
import {
  createPetVaccination,
  deletePetVaccination,
  linkVetToPet,
  listPetVaccinations,
  listPetsByCustomer,
  listVaccines,
  listVets,
  listVetsForPet,
  setPrimaryVetForPet,
  unlinkVetFromPet,
  updatePet,
} from '@/lib/api';
import type { Vaccine } from '@/lib/api';
import type { Pet, PetVetLink, UpdatePetInput, Vet } from '@/lib/types';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types used only in this file
// ---------------------------------------------------------------------------

interface StaffVaccination {
  id: string;
  vaccineId: string;
  administeredOn: string | null;
  expiresOn: string | null;
  notes: string | null;
}

// ---------------------------------------------------------------------------
// Shared field components
// ---------------------------------------------------------------------------

function Field({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label}
        {optional && (
          <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="mb-4 border-b border-border pb-2 text-sm font-semibold text-foreground/80">
      {title}
    </h2>
  );
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const selectCls =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30';
const textareaCls =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30';

// ---------------------------------------------------------------------------
// Form state (text fields only — structured fields managed separately)
// ---------------------------------------------------------------------------

interface TextFormState {
  name: string;
  species: string;
  breed: string;
  sizeCategory: string;
  dateOfBirth: string;
  sex: string;
  neutered: string;
  color: string;
  microchip: string;
  microchipRegisteredWith: string;
  microchipRegistrationDate: string;
  notes: string;
  weightKg: string;
  feedingInstructions: string;
  medicalConditions: string;
  behaviorNotes: string;
  specialInstructions: string;
  insuranceProvider: string;
  insurancePolicyNumber: string;
  insuranceExpiresOn: string;
  // Migration 0100 — licensing register fields.
  localEmergencyContactName: string;
  localEmergencyContactPhone: string;
  wormingLastOn: string;
  fleaLastOn: string;
}

function fromPet(pet: Pet): TextFormState {
  return {
    name: pet.name ?? '',
    species: pet.species ?? '',
    breed: pet.breed ?? '',
    sizeCategory: pet.sizeCategory ?? '',
    dateOfBirth: pet.dateOfBirth ?? '',
    sex: pet.sex ?? '',
    neutered: pet.neutered != null ? String(pet.neutered) : '',
    color: pet.color ?? '',
    microchip: pet.microchip ?? '',
    microchipRegisteredWith: pet.microchipRegisteredWith ?? '',
    microchipRegistrationDate: pet.microchipRegistrationDate ?? '',
    notes: pet.notes ?? '',
    weightKg: pet.weightKg != null ? String(pet.weightKg) : '',
    feedingInstructions: pet.feedingInstructions ?? '',
    medicalConditions: pet.medicalConditions ?? '',
    behaviorNotes: pet.behaviorNotes ?? '',
    specialInstructions: pet.specialInstructions ?? '',
    insuranceProvider: pet.insuranceProvider ?? '',
    insurancePolicyNumber: pet.insurancePolicyNumber ?? '',
    insuranceExpiresOn: pet.insuranceExpiresOn ?? '',
    localEmergencyContactName: pet.localEmergencyContactName ?? '',
    localEmergencyContactPhone: pet.localEmergencyContactPhone ?? '',
    wormingLastOn: pet.wormingLastOn ?? '',
    fleaLastOn: pet.fleaLastOn ?? '',
  };
}

function toNullable(s: string): string | null {
  return s.trim() || null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function vaccineStatusBadge(expiresOn: string | null): { label: string; cls: string } {
  if (!expiresOn) return { label: 'No expiry', cls: 'text-muted-foreground' };
  const today = new Date();
  const exp = new Date(expiresOn);
  const diffDays = Math.floor((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return { label: 'Expired', cls: 'text-destructive' };
  if (diffDays <= 30) return { label: 'Expiring soon', cls: 'text-amber-600' };
  return { label: 'Valid', cls: 'text-emerald-600' };
}

export function PetEdit() {
  const { customerId, petId } = useParams<{ customerId: string; petId: string }>();
  const navigate = useNavigate();

  const [pet, setPet] = useState<Pet | null>(null);
  const [form, setForm] = useState<TextFormState | null>(null);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [vaccinations, setVaccinations] = useState<StaffVaccination[]>([]);
  const [vaccineCatalogue, setVaccineCatalogue] = useState<Vaccine[]>([]);
  const [showVaccineForm, setShowVaccineForm] = useState(false);
  const [vaccineForm, setVaccineForm] = useState({
    vaccineId: '',
    administeredOn: '',
    expiresOn: '',
    notes: '',
  });
  const [vaccineBusy, setVaccineBusy] = useState(false);

  const [linkedVets, setLinkedVets] = useState<PetVetLink[]>([]);
  const [allVets, setAllVets] = useState<Vet[]>([]);
  const [showVetPicker, setShowVetPicker] = useState(false);
  const [vetPickerVetId, setVetPickerVetId] = useState('');
  const [vetBusy, setVetBusy] = useState(false);

  const load = useCallback(async () => {
    if (!customerId || !petId) return;
    setLoading(true);
    try {
      const [pets, vacsResult, links, vaccineList, vetsList] = await Promise.all([
        listPetsByCustomer(customerId),
        listPetVaccinations(petId).catch(() => ({ items: [] as StaffVaccination[] })),
        listVetsForPet(petId).catch(() => [] as PetVetLink[]),
        listVaccines().catch(() => ({ vaccines: [] as Vaccine[] })),
        listVets().catch(() => [] as Vet[]),
      ]);
      const found = pets.find((p) => p.id === petId);
      if (!found) {
        setNotFound(true);
        return;
      }
      setPet(found);
      setForm(fromPet(found));
      setAllergies(parseAllergiesJson(found.allergiesJson));
      setMedications(parseMedicationsJson(found.medicationsJson));
      setVaccinations(vacsResult.items);
      setLinkedVets(links);
      setVaccineCatalogue(vaccineList.vaccines);
      setAllVets(vetsList);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [customerId, petId]);

  useEffect(() => {
    void load();
  }, [load]);

  function set(field: keyof TextFormState, value: string) {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form || !petId) return;
    setBusy(true);
    setError(null);
    try {
      const weightKgParsed = form.weightKg.trim() ? Number(form.weightKg) : null;
      const neuteredParsed = form.neutered === '1' ? 1 : form.neutered === '0' ? 0 : null;

      const trimmedName = form.name.trim();
      const speciesValue = form.species === 'dog' || form.species === 'cat' ? form.species : null;
      const sexRaw = toNullable(form.sex);
      const sexValue =
        sexRaw === 'male' || sexRaw === 'female' || sexRaw === 'unknown' ? sexRaw : null;

      const patch: UpdatePetInput = {
        ...(trimmedName ? { name: trimmedName } : {}),
        ...(speciesValue ? { species: speciesValue } : {}),
        breed: toNullable(form.breed),
        sizeCategory: toNullable(form.sizeCategory),
        dateOfBirth: toNullable(form.dateOfBirth),
        sex: sexValue,
        neutered: neuteredParsed,
        color: toNullable(form.color),
        microchip: toNullable(form.microchip),
        microchipRegisteredWith: toNullable(form.microchipRegisteredWith),
        microchipRegistrationDate: toNullable(form.microchipRegistrationDate),
        notes: toNullable(form.notes),
        weightKg: weightKgParsed,
        feedingInstructions: toNullable(form.feedingInstructions),
        allergiesJson: serializeAllergiesJson(allergies),
        medicationsJson: serializeMedicationsJson(medications),
        medicalConditions: toNullable(form.medicalConditions),
        behaviorNotes: toNullable(form.behaviorNotes),
        specialInstructions: toNullable(form.specialInstructions),
        insuranceProvider: toNullable(form.insuranceProvider),
        insurancePolicyNumber: toNullable(form.insurancePolicyNumber),
        insuranceExpiresOn: toNullable(form.insuranceExpiresOn),
        localEmergencyContactName: toNullable(form.localEmergencyContactName),
        localEmergencyContactPhone: toNullable(form.localEmergencyContactPhone),
        wormingLastOn: toNullable(form.wormingLastOn),
        fleaLastOn: toNullable(form.fleaLastOn),
      };
      await updatePet(petId, patch);
      navigate(`/customers/${customerId}`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleAddVaccination() {
    if (!petId || !vaccineForm.vaccineId) return;
    setVaccineBusy(true);
    setError(null);
    try {
      const { vaccination: vac } = await createPetVaccination(petId, {
        vaccineId: vaccineForm.vaccineId,
        administeredOn: vaccineForm.administeredOn || null,
        expiresOn: vaccineForm.expiresOn || null,
        notes: vaccineForm.notes || null,
      });
      setVaccinations((prev) => [...prev, vac as StaffVaccination]);
      setVaccineForm({ vaccineId: '', administeredOn: '', expiresOn: '', notes: '' });
      setShowVaccineForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add vaccination');
    } finally {
      setVaccineBusy(false);
    }
  }

  async function handleDeleteVaccination(id: string) {
    if (!petId) return;
    if (!window.confirm('Delete this vaccination record?')) return;
    setError(null);
    try {
      await deletePetVaccination(petId, id);
      setVaccinations((prev) => prev.filter((v) => v.id !== id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete vaccination');
    }
  }

  async function handleLinkVet() {
    if (!petId || !vetPickerVetId) return;
    setVetBusy(true);
    try {
      const link = await linkVetToPet(petId, vetPickerVetId);
      setLinkedVets((prev) => [...prev, link]);
      setVetPickerVetId('');
      setShowVetPicker(false);
    } finally {
      setVetBusy(false);
    }
  }

  async function handleUnlinkVet(vetId: string) {
    if (!petId) return;
    if (!window.confirm('Unlink this vet from the pet?')) return;
    await unlinkVetFromPet(petId, vetId);
    setLinkedVets((prev) => prev.filter((l) => l.vetId !== vetId));
  }

  async function handleSetPrimaryVet(vetId: string) {
    if (!petId) return;
    const updated = await setPrimaryVetForPet(petId, vetId);
    setLinkedVets((prev) =>
      prev.map((l) =>
        l.vetId === vetId
          ? { ...l, isPrimary: 1 }
          : l.petId === updated.petId
            ? { ...l, isPrimary: 0 }
            : l,
      ),
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (notFound || !pet || !form) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
        <Link
          to={`/customers/${customerId}`}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          <ArrowLeft className="size-4" strokeWidth={1.75} />
          Back to customer
        </Link>
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="font-medium">Pet not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 md:px-10">
      <Link
        to={`/customers/${customerId}`}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} />
        Back to customer
      </Link>

      <h1 className="mb-6 font-heading text-2xl font-medium tracking-tight">Edit — {pet.name}</h1>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-8">
        {/* About this pet */}
        <section>
          <SectionHeader title="About this pet" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-name" label="Name">
              <input
                id="pet-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-species" label="Species">
              <select
                id="pet-species"
                required
                value={form.species}
                onChange={(e) => set('species', e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
              </select>
            </Field>
            <Field id="pet-breed" label="Breed" optional>
              <input
                id="pet-breed"
                type="text"
                value={form.breed}
                onChange={(e) => set('breed', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-size" label="Size category" optional>
              <select
                id="pet-size"
                value={form.sizeCategory}
                onChange={(e) => set('sizeCategory', e.target.value)}
                className={selectCls}
              >
                <option value="">Select…</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="extra_large">Extra large</option>
              </select>
            </Field>
            <Field id="pet-dob" label="Date of birth" optional>
              <input
                id="pet-dob"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => set('dateOfBirth', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-sex" label="Sex" optional>
              <select
                id="pet-sex"
                value={form.sex}
                onChange={(e) => set('sex', e.target.value)}
                className={selectCls}
              >
                <option value="">Unknown</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </Field>
            <Field id="pet-neutered" label="Neutered / spayed" optional>
              <select
                id="pet-neutered"
                value={form.neutered}
                onChange={(e) => set('neutered', e.target.value)}
                className={selectCls}
              >
                <option value="">Unknown</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </Field>
            <Field id="pet-color" label="Colour" optional>
              <input
                id="pet-color"
                type="text"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-weight" label="Weight (kg)" optional>
              <input
                id="pet-weight"
                type="number"
                min="0"
                step="0.1"
                value={form.weightKg}
                onChange={(e) => set('weightKg', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Microchip */}
        <section>
          <SectionHeader title="Microchip" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-microchip" label="Chip number" optional>
              <input
                id="pet-microchip"
                type="text"
                value={form.microchip}
                onChange={(e) => set('microchip', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-chip-registry" label="Registered with" optional>
              <input
                id="pet-chip-registry"
                type="text"
                value={form.microchipRegisteredWith}
                onChange={(e) => set('microchipRegisteredWith', e.target.value)}
                placeholder="e.g. Petlog, Identibase"
                className={inputCls}
              />
            </Field>
            <Field id="pet-chip-date" label="Registration date" optional>
              <input
                id="pet-chip-date"
                type="date"
                value={form.microchipRegistrationDate}
                onChange={(e) => set('microchipRegistrationDate', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Diet & feeding */}
        <section>
          <SectionHeader title="Diet & feeding" />
          <div className="grid grid-cols-1 gap-4">
            <Field id="pet-feeding" label="Feeding instructions" optional>
              <textarea
                id="pet-feeding"
                value={form.feedingInstructions}
                onChange={(e) => set('feedingInstructions', e.target.value)}
                rows={3}
                placeholder="Food name, portions, schedule…"
                className={textareaCls}
              />
            </Field>
            <div>
              <label
                htmlFor="pet-allergies"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Allergies
                <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
                  (optional)
                </span>
              </label>
              <AllergyChipInput id="pet-allergies" value={allergies} onChange={setAllergies} />
              <p className="mt-1 text-xs text-muted-foreground">
                Type and press Enter or comma to add each allergy.
              </p>
            </div>
            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Medications
                <span className="ml-1 normal-case tracking-normal text-muted-foreground/60">
                  (optional)
                </span>
              </p>
              <MedicationEditor value={medications} onChange={setMedications} />
            </div>
          </div>
        </section>

        {/* Behaviour & medical */}
        <section>
          <SectionHeader title="Behaviour & medical" />
          <div className="grid grid-cols-1 gap-4">
            <Field id="pet-conditions" label="Medical conditions" optional>
              <textarea
                id="pet-conditions"
                value={form.medicalConditions}
                onChange={(e) => set('medicalConditions', e.target.value)}
                rows={2}
                className={textareaCls}
              />
            </Field>
            <Field id="pet-behavior" label="Behaviour notes" optional>
              <textarea
                id="pet-behavior"
                value={form.behaviorNotes}
                onChange={(e) => set('behaviorNotes', e.target.value)}
                rows={3}
                placeholder="Separation anxiety, reactive to other dogs…"
                className={textareaCls}
              />
            </Field>
            <Field id="pet-special" label="Special instructions" optional>
              <textarea
                id="pet-special"
                value={form.specialInstructions}
                onChange={(e) => set('specialInstructions', e.target.value)}
                rows={2}
                placeholder="Grooming preferences, walking instructions…"
                className={textareaCls}
              />
            </Field>
            <Field id="pet-notes" label="General notes" optional>
              <textarea
                id="pet-notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={2}
                className={textareaCls}
              />
            </Field>
          </div>
        </section>

        {/* Insurance */}
        <section>
          <SectionHeader title="Insurance" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-insurer" label="Provider" optional>
              <input
                id="pet-insurer"
                type="text"
                value={form.insuranceProvider}
                onChange={(e) => set('insuranceProvider', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-policy" label="Policy number" optional>
              <input
                id="pet-policy"
                type="text"
                value={form.insurancePolicyNumber}
                onChange={(e) => set('insurancePolicyNumber', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-insurance-expires" label="Policy expires" optional>
              <input
                id="pet-insurance-expires"
                type="date"
                value={form.insuranceExpiresOn}
                onChange={(e) => set('insuranceExpiresOn', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Licensing register — migration 0100 */}
        <section>
          <SectionHeader title="Local emergency contact and parasite treatment" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pet-local-ec-name" label="Local emergency contact" optional>
              <input
                id="pet-local-ec-name"
                type="text"
                value={form.localEmergencyContactName}
                onChange={(e) => set('localEmergencyContactName', e.target.value)}
                placeholder="Someone nearby who can act for the owner"
                className={inputCls}
              />
            </Field>
            <Field id="pet-local-ec-phone" label="Local contact phone" optional>
              <input
                id="pet-local-ec-phone"
                type="tel"
                value={form.localEmergencyContactPhone}
                onChange={(e) => set('localEmergencyContactPhone', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-worming" label="Last wormed" optional>
              <input
                id="pet-worming"
                type="date"
                value={form.wormingLastOn}
                onChange={(e) => set('wormingLastOn', e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field id="pet-flea" label="Last flea treatment" optional>
              <input
                id="pet-flea"
                type="date"
                value={form.fleaLastOn}
                onChange={(e) => set('fleaLastOn', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        </section>

        {/* Vaccinations */}
        <section>
          <SectionHeader title="Vaccinations" />
          <div className="space-y-2">
            {vaccinations.length === 0 && !showVaccineForm && (
              <p className="text-sm text-muted-foreground">No vaccination records yet.</p>
            )}
            {vaccinations.map((vac) => {
              const vaccine = vaccineCatalogue.find((v) => v.id === vac.vaccineId);
              const badge = vaccineStatusBadge(vac.expiresOn);
              return (
                <div
                  key={vac.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {vaccine ? vaccine.name : vac.vaccineId}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {vac.administeredOn ? `Given ${vac.administeredOn}` : 'Date unknown'}
                      {vac.expiresOn ? ` · Expires ${vac.expiresOn}` : ''}
                    </span>
                  </div>
                  <span className={`text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                  <button
                    type="button"
                    onClick={() => void handleDeleteVaccination(vac.id)}
                    className="ml-1 shrink-0 rounded p-1 text-muted-foreground hover:text-destructive press"
                    aria-label="Delete vaccination"
                  >
                    <Trash2 className="size-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              );
            })}
          </div>

          {showVaccineForm && (
            <form
              className="mt-3 space-y-3 rounded-md border border-border bg-surface p-3"
              onSubmit={(e) => {
                e.preventDefault();
                void handleAddVaccination();
              }}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field id="vac-vaccine" label="Vaccine">
                  <select
                    id="vac-vaccine"
                    value={vaccineForm.vaccineId}
                    onChange={(e) => setVaccineForm((f) => ({ ...f, vaccineId: e.target.value }))}
                    className={selectCls}
                  >
                    <option value="">Select vaccine…</option>
                    {vaccineCatalogue.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="vac-administered" label="Administered on" optional>
                  <input
                    id="vac-administered"
                    type="date"
                    value={vaccineForm.administeredOn}
                    onChange={(e) =>
                      setVaccineForm((f) => ({ ...f, administeredOn: e.target.value }))
                    }
                    className={inputCls}
                  />
                </Field>
                <Field id="vac-expires" label="Expires on" optional>
                  <input
                    id="vac-expires"
                    type="date"
                    value={vaccineForm.expiresOn}
                    onChange={(e) => setVaccineForm((f) => ({ ...f, expiresOn: e.target.value }))}
                    className={inputCls}
                  />
                </Field>
                <Field id="vac-notes" label="Notes" optional>
                  <input
                    id="vac-notes"
                    type="text"
                    value={vaccineForm.notes}
                    onChange={(e) => setVaccineForm((f) => ({ ...f, notes: e.target.value }))}
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={!vaccineForm.vaccineId || vaccineBusy}
                  className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
                >
                  {vaccineBusy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVaccineForm(false);
                    setVaccineForm({
                      vaccineId: '',
                      administeredOn: '',
                      expiresOn: '',
                      notes: '',
                    });
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-secondary press"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {!showVaccineForm && (
            <button
              type="button"
              onClick={() => setShowVaccineForm(true)}
              className="mt-3 flex items-center gap-1.5 text-sm text-primary hover:underline press"
            >
              <Plus className="size-3.5" strokeWidth={1.75} />
              Add vaccination
            </button>
          )}
        </section>

        {/* Linked vets */}
        <section>
          <SectionHeader title="Linked vets" />
          <div className="space-y-2">
            {linkedVets.length === 0 && !showVetPicker && (
              <p className="text-sm text-muted-foreground">No vets linked yet.</p>
            )}
            {linkedVets.map((link) => {
              const vet = link.vet ?? allVets.find((v) => v.id === link.vetId);
              return (
                <div
                  key={link.vetId}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {vet ? vet.practiceName : link.vetId}
                    </span>
                    {vet?.vetName && (
                      <span className="block text-xs text-muted-foreground">{vet.vetName}</span>
                    )}
                  </div>
                  {link.isPrimary === 1 && (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium text-primary">
                      Primary
                    </span>
                  )}
                  <div className="flex shrink-0 items-center gap-1">
                    {link.isPrimary !== 1 && (
                      <button
                        type="button"
                        onClick={() => void handleSetPrimaryVet(link.vetId)}
                        className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground press"
                      >
                        Set primary
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void handleUnlinkVet(link.vetId)}
                      className="rounded p-1 text-muted-foreground hover:text-destructive press"
                      aria-label="Unlink vet"
                    >
                      <Trash2 className="size-3.5" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {showVetPicker && (
            <div className="mt-3 flex items-center gap-2">
              <select
                value={vetPickerVetId}
                onChange={(e) => setVetPickerVetId(e.target.value)}
                className={`${selectCls} max-w-xs`}
              >
                <option value="">Select vet…</option>
                {allVets
                  .filter((v) => v.isActive === 1 && !linkedVets.some((l) => l.vetId === v.id))
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.practiceName}
                      {v.vetName ? ` — ${v.vetName}` : ''}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={!vetPickerVetId || vetBusy}
                onClick={() => void handleLinkVet()}
                className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
              >
                {vetBusy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
                Link
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowVetPicker(false);
                  setVetPickerVetId('');
                }}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-secondary press"
              >
                Cancel
              </button>
            </div>
          )}

          {!showVetPicker && (
            <div className="mt-3">
              {allVets.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Add vets first in{' '}
                  <Link to="/settings/vets" className="text-primary hover:underline">
                    Settings &rarr; Vets
                  </Link>
                  .
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowVetPicker(true)}
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline press"
                >
                  <Plus className="size-3.5" strokeWidth={1.75} />
                  Link vet
                </button>
              )}
            </div>
          )}
        </section>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 backdrop-blur px-4 py-3 md:-mx-8 md:px-8">
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !form.name.trim() || !form.species}
              className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
              {busy ? 'Saving…' : 'Save changes'}
            </button>
            <Link
              to={`/customers/${customerId}`}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary press"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
